import type { ShipstarConfig, ChangelogResponse, BlogPostResponse, KbArticlesResponse, BannerResponse } from "./types";
import { ShipstarError, AuthError, NotFoundError, RateLimitError } from "./types";
import { debugLog } from "./utils";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isValidChangelogResponse(data: unknown): data is ChangelogResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return Array.isArray(d.entries);
}

function isValidBlogResponse(data: unknown): data is BlogPostResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return typeof d.content === "string";
}

function isValidBlogListResponse(data: unknown): data is BlogPostResponse[] {
  return Array.isArray(data) && data.every(isValidBlogResponse);
}

function isValidKbResponse(data: unknown): data is KbArticlesResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return Array.isArray(d.articles);
}

function isValidBannerResponse(data: unknown): data is BannerResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return typeof d.headline === "string" && typeof d.slug === "string";
}

async function fetchWithRetry<T>(
  config: ShipstarConfig,
  url: string,
  validate: (data: unknown) => data is T,
): Promise<T> {
  debugLog(config, "Fetching:", url);
  const startTime = performance.now();

  let lastError: Error | null = null;

  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) {
      debugLog(config, "Retrying after 1s...");
      await delay(1000);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    try {
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: controller.signal,
        referrerPolicy: "no-referrer-when-downgrade",
      });

      clearTimeout(timeoutId);

      const elapsed = Math.round(performance.now() - startTime);
      debugLog(config, `Response: ${res.status} (${elapsed}ms)`);

      if (res.status === 401 || res.status === 403) {
        throw new AuthError();
      }
      if (res.status === 429) {
        throw new RateLimitError();
      }
      if (res.status >= 500) {
        lastError = new ShipstarError(
          `[Shipstar] Server error: ${res.status}`
        );
        continue;
      }
      if (res.status === 404) {
        throw new NotFoundError();
      }
      if (!res.ok) {
        throw new ShipstarError(
          `[Shipstar] Request failed with status ${res.status}`
        );
      }

      let data: unknown;
      try {
        data = await res.json();
      } catch {
        throw new ShipstarError("[Shipstar] Invalid JSON response");
      }

      if (!validate(data)) {
        throw new ShipstarError("[Shipstar] Malformed response data");
      }

      return data;
    } catch (err) {
      clearTimeout(timeoutId);

      if (err instanceof AuthError || err instanceof RateLimitError) {
        throw err;
      }
      if (err instanceof ShipstarError) {
        if (
          err.message.includes("Request failed") ||
          err.message.includes("Invalid JSON") ||
          err.message.includes("Malformed")
        ) {
          throw err;
        }
      }

      lastError =
        err instanceof Error
          ? err
          : new ShipstarError("[Shipstar] Network error");

      if (attempt === 0) continue;
    }
  }

  throw lastError || new ShipstarError("[Shipstar] Request failed");
}

export async function fetchChangelog(
  config: ShipstarConfig
): Promise<ChangelogResponse> {
  const url = `${config.apiBaseUrl}/api/v1/changelog/${encodeURIComponent(config.apiKey)}`;
  return fetchWithRetry(config, url, isValidChangelogResponse);
}

export async function fetchBlogPost(
  config: ShipstarConfig
): Promise<BlogPostResponse> {
  const url = `${config.apiBaseUrl}/api/v1/blog/${encodeURIComponent(config.apiKey)}`;
  return fetchWithRetry(config, url, isValidBlogResponse);
}

export async function fetchBlogPosts(
  config: ShipstarConfig
): Promise<BlogPostResponse[]> {
  const url = `${config.apiBaseUrl}/api/v1/blogs`;
  return fetchWithRetry(config, url, isValidBlogListResponse);
}

export async function fetchKbArticles(
  config: ShipstarConfig
): Promise<KbArticlesResponse> {
  const url = `${config.apiBaseUrl}/api/v1/kb/${encodeURIComponent(config.apiKey)}`;
  return fetchWithRetry(config, url, isValidKbResponse);
}

/**
 * The live banner for an embed key, or null when none is live — a normal
 * state (never published, replaced, past its take-down time), not an error.
 */
export async function fetchBanner(
  config: ShipstarConfig
): Promise<BannerResponse | null> {
  const url = `${config.apiBaseUrl}/api/v1/banner/${encodeURIComponent(config.apiKey)}`;
  try {
    return await fetchWithRetry(config, url, isValidBannerResponse);
  } catch (err) {
    if (err instanceof NotFoundError) {
      debugLog(config, "No banner is live for this key.");
      return null;
    }
    throw err;
  }
}

export async function subscribeToChangelog(
  config: ShipstarConfig,
  email: string
): Promise<{ message: string }> {
  const url = `${config.apiBaseUrl}/api/v1/changelog/subscribe`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, public_slug: config.apiKey }),
  });

  if (res.status === 429) {
    throw new RateLimitError();
  }
  if (!res.ok) {
    throw new ShipstarError(`[Shipstar] Subscribe failed with status ${res.status}`);
  }

  return res.json();
}
